import { useState, useRef, useEffect } from 'react';
import { RUMAAL_RANKS } from '../../utils/constants';
import { getRumaalColor } from '../../utils/colors';

/**
 * A small colored "handkerchief" (rumaal) swatch — a folded-cloth triangle in
 * the belt's color. White gets a visible outline so it doesn't vanish on a
 * white background.
 */
const RumaalSwatch = ({ rank, size = 14 }) => {
  const color = getRumaalColor(rank);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      className="shrink-0"
      aria-hidden="true"
    >
      {/* Folded handkerchief: a square knotted at the top with a hanging point */}
      <path
        d="M2 4 L14 4 L8 15 Z"
        fill={color}
        stroke="#9CA3AF"
        strokeWidth={rank === 'White' ? 1 : 0.5}
      />
      <rect
        x="6"
        y="1.5"
        width="4"
        height="3"
        rx="1"
        fill={color}
        stroke="#9CA3AF"
        strokeWidth={rank === 'White' ? 1 : 0.5}
      />
    </svg>
  );
};

/**
 * Rumaal-rank picker with a color indicator next to each belt name.
 *
 * Replaces a plain native <select> (which can't render colored swatches in its
 * option list). Emits the same rank string via onChange, so it's a drop-in
 * replacement — no data/schema change.
 *
 * @param {string} value     selected rank (one of RUMAAL_RANKS)
 * @param {(rank: string) => void} onChange
 * @param {'xs'|'sm'} [size] visual size to match the surrounding controls
 * @param {boolean} [fullWidth] stretch to fill a grid column, so it lines up
 *                              with the status select sitting beside it
 */
const RumaalRankSelect = ({ value, onChange, size = 'sm', fullWidth = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const current = value || 'White';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const select = (rank) => {
    onChange(rank);
    setIsOpen(false);
  };

  const textSize = size === 'xs' ? 'text-xs' : 'text-sm';
  const pad = size === 'xs' ? 'px-1.5 py-0.5' : 'px-2 py-1';
  const swatchSize = size === 'xs' ? 12 : 14;

  return (
    <div className={`relative ${fullWidth ? 'w-full' : ''}`} ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className={`flex items-center gap-1.5 rounded-md border border-gray-200 bg-white ${pad} ${textSize} text-gray-800 outline-none transition-shadow focus:border-purple-400 focus:ring-2 focus:ring-purple-500/15 ${
          fullWidth ? 'w-full justify-between' : ''
        }`}
      >
        <RumaalSwatch rank={current} size={swatchSize} />
        <span className={fullWidth ? 'mr-auto ml-1.5' : ''}>{current}</span>
        <svg
          className={`h-3 w-3 shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <ul
          className="absolute z-20 mt-1 min-w-full overflow-hidden rounded-md border border-gray-200 bg-white py-1 shadow-lg ring-1 ring-black/5"
          role="listbox"
        >
          {RUMAAL_RANKS.map((rank) => (
            <li
              key={rank}
              role="option"
              aria-selected={current === rank}
              onClick={() => select(rank)}
              className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 ${textSize} transition-colors hover:bg-purple-50 ${
                current === rank ? 'bg-purple-50/60 font-semibold' : ''
              }`}
            >
              <RumaalSwatch rank={rank} size={swatchSize} />
              <span>{rank}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default RumaalRankSelect;
